<template>
  <span
    :is="is"
    ref="button"
    v-bind="link"
    :class="classes"
    @click="click"
  >
    <spinner-animated
      v-if="loading"
      color="white"
      :size="spinnerSize"
    />

    <font-awesome-icon
      v-else-if="icon"
      :icon="icon"
      class="former-button-icon"
    />

    <slot v-if="!loading && !iconed" />
  </span>
</template>

<script>
  import SpinnerAnimated from '@/components/Spinner/Animated.vue';

  export default {
    name: 'FormerButton',
    components: {
      SpinnerAnimated,
    },
    props: {
      color: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      readOnly: {
        type: Boolean,
        default: false,
      },
      iconed: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: [String, Array],
        default: '',
      },
      iconSize: {
        type: String,
        default: '',
      },
      loading: {
        type: Boolean,
        default: false,
      },
      size: {
        type: String,
        default: '',
      },
      spinnerSize: {
        type: String,
        default: '',
      },
      padded: {
        type: Boolean,
        default: false,
      },
      nowrap: {
        type: Boolean,
        default: false,
      },
      href: {
        type: [String, Object],
        default: '',
      },
    },
    computed: {
      is() {
        if (!this.disabled && this.href) {
          return 'router-link';
        }

        return 'button';
      },
      link() {
        if (this.href) {
          return {
            to: this.href,
          };
        }

        return {};
      },
      classes() {
        const classes = ['former-button'];

        if (this.iconSize) {
          classes.push(`former-button-icon-size-${this.iconSize}`);
        }

        if (this.iconed) {
          classes.push('former-button-iconed');
        }

        classes.push(`former-button-size-${this.size || 'medium'}`);
        classes.push(`former-button-color-${this.color || 'green'}`);

        if (this.padded) {
          classes.push('former-button-padded');
        }

        if (this.nowrap) {
          classes.push('former-button-nowrap');
        }

        if (this.loading) {
          classes.push('state--loading');
        }

        if (this.disabled || this.readOnly) {
          classes.push('state--disabled');
        }

        return classes;
      },
    },
    methods: {
      click() {
        if (this.disabled || this.readOnly || this.loading) {
          return;
        }

        this.blur();
        this.$emit('click');
      },
      focus() {
        if (this.$refs.button) {
          this.$refs.button.focus();
        }
      },
      blur() {
        if (this.$refs.button) {
          this.$refs.button.blur();
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/button.scss';
  @import '@/assets/stylesheets/_variables.scss';

  .former-button {
    @include button-reset;
    @include cordel-button;
  }

  .former-button-iconed {
    padding: var(--cordel-spacing-xxxs) !important;
  }

  .former-button-icon {
    margin-right: 0.125em;

    .former-button-iconed & {
      margin-right: 0;
    }
  }

  .former-button-size-medium {
    @include button-size(
      var(--cordel-font-size-m),
      var(--cordel-font-size-s),
      var(--cordel-spacing-xxxs) var(--cordel-spacing-l),
      var(--cordel-spacing-xxxs),
    );
  }

  .former-button-size-small,
  .former-button-size-smaller {
    @include button-size(
      var(--cordel-font-size-s),
      var(--cordel-font-size-s),
      var(--cordel-spacing-micro) var(--cordel-spacing-xxxs),
      var(--cordel-spacing-nano),
    );
  }

  .former-button-color-green {
    @include button-color(
      var(--cordel-color-brand-primary),
      var(--cordel-color-brand-dark),
      var(--cordel-color-constant-light),
    );
  }

  .former-button-color-gray {
    @include button-color(
      var(--cordel-color-neutral-dark),
      var(--cordel-color-constant-dark),
      var(--cordel-color-constant-light),
    );
  }

  .former-button-color-gray-dark {
    @include button-color(
      var(--cordel-color-neutral-darker),
      var(--cordel-color-neutral-heavy),
      var(--cordel-color-constant-light),
    );
  }

  .former-button-color-blue {
    @include button-color(
      var(--cordel-color-highlight-primary),
      var(--cordel-color-highlight-dark),
      var(--cordel-color-constant-dark),
    );
  }

  .former-button-color-red {
    @include button-color(
      var(--cordel-color-alert-danger),
      var(--cordel-color-alert-danger-dark),
      var(--cordel-color-constant-light),
    );
  }

  .former-button-padded {
    padding-left: $gutter-big;
    padding-right: $gutter-big;
  }

  .former-button-nowrap {
    white-space: nowrap;
  }

  .former-button-loading {
    background-color: var(--cordel-color-neutral-light);
    color: var(--cordel-color-neutral-primary);

    cursor: progress;
  }

  .former-button-disabled {
    background-color: var(--cordel-color-neutral-light);
    color: var(--cordel-color-neutral-primary);

    cursor: default;
  }
</style>
